import type { SquishLayout } from '@usespaceui/squishmoji';

export type GazePoint = { x: number; y: number };
const outlineCache = new Map<string, GazePoint[]>();

/** Sample SquishEngine's M/C/Z body outline, in its native face coordinates. */
function outline(body: string): GazePoint[] {
  const cached = outlineCache.get(body);
  if (cached) return cached;
  const tokens = body.match(/[A-Za-z]|-?\d*\.?\d+(?:e[+-]?\d+)?/gi) ?? [];
  const points: GazePoint[] = [];
  let current = { x: 0, y: 0 };
  let index = 0;
  const point = () => ({
    x: Number(tokens[index++]),
    y: Number(tokens[index++]),
  });
  while (index < tokens.length) {
    const command = tokens[index++];
    if (command === 'M') {
      current = point();
      points.push(current);
    } else if (command === 'C') {
      const start = current;
      const first = point();
      const second = point();
      const end = point();
      for (let step = 1; step <= 4; step++) {
        const t = step / 4;
        const u = 1 - t;
        points.push({
          x:
            u ** 3 * start.x +
            3 * u ** 2 * t * first.x +
            3 * u * t ** 2 * second.x +
            t ** 3 * end.x,
          y:
            u ** 3 * start.y +
            3 * u ** 2 * t * first.y +
            3 * u * t ** 2 * second.y +
            t ** 3 * end.y,
        });
      }
      current = end;
    } else if (command !== 'Z') return [];
  }
  if (!points.every((p) => Number.isFinite(p.x) && Number.isFinite(p.y)))
    return [];
  outlineCache.set(body, points);
  if (outlineCache.size > 16) {
    const oldest = outlineCache.keys().next().value;
    if (oldest !== undefined) outlineCache.delete(oldest);
  }
  return points;
}

function inside(point: GazePoint, polygon: GazePoint[]): boolean {
  let contained = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const a = polygon[i]!;
    const b = polygon[j]!;
    if (
      a.y > point.y !== b.y > point.y &&
      point.x < ((b.x - a.x) * (point.y - a.y)) / (b.y - a.y) + a.x
    )
      contained = !contained;
  }
  return contained;
}

/** Both fitted eyes, including a small inset, must stay inside the body. */
export function containAvatarGaze(
  target: GazePoint,
  layout: SquishLayout,
  relativeToEyes = false
): GazePoint {
  if (!Number.isFinite(target.x) || !Number.isFinite(target.y))
    return { x: 0, y: 0 };
  const polygon = outline(layout.body);
  if (polygon.length < 3) return { x: 0, y: 0 };
  const cornersAt = (inset: number) =>
    layout.eyes.flatMap((eye) => {
      const [a, b, c, d, x, y] = eye.matrix;
      return [-1, 0, 1].flatMap((horizontal) =>
        [-1, 0, 1].map((vertical) => {
          const px = horizontal * (eye.width / 2 + inset);
          const py = vertical * (eye.height / 2 + inset);
          return { x: a * px + c * py + x, y: b * px + d * py + y };
        })
      );
    });
  let corners = cornersAt(8);
  for (const inset of [4, 0]) {
    if (corners.every((point) => inside(point, polygon))) break;
    corners = cornersAt(inset);
  }
  if (
    !corners.length ||
    !corners.every((p) => Number.isFinite(p.x) && Number.isFinite(p.y))
  )
    return { x: 0, y: 0 };
  // Aim the pair's centre at the cursor rather than its original placement.
  const offset = relativeToEyes
    ? target
    : {
        x:
          target.x -
          (Math.min(...corners.map((p) => p.x)) +
            Math.max(...corners.map((p) => p.x))) /
            2,
        y:
          target.y -
          (Math.min(...corners.map((p) => p.y)) +
            Math.max(...corners.map((p) => p.y))) /
            2,
      };
  const fits = (amount: number) =>
    corners.every((p) =>
      inside(
        { x: p.x + offset.x * amount, y: p.y + offset.y * amount },
        polygon
      )
    );
  if (!fits(0)) return { x: 0, y: 0 };
  // Search outward before refining, so concave shapes cannot jump a gap.
  let low = 0;
  let high = 1;
  for (let step = 1; step <= 16; step++) {
    const amount = step / 16;
    if (!fits(amount)) {
      high = amount;
      break;
    }
    low = amount;
  }
  for (let step = 0; step < 10; step++) {
    const middle = (low + high) / 2;
    if (fits(middle)) low = middle;
    else high = middle;
  }
  return { x: offset.x * low, y: offset.y * low };
}

/** Rebound an existing translation immediately when the face changes. */
export function constrainAvatarGazeOffset(
  offset: GazePoint,
  layout: SquishLayout
) {
  return containAvatarGaze(offset, layout, true);
}
